import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Upload, AlertCircle, PartyPopper, CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import CSVMapper from "@/components/csv/CSVMapper";
import { Lead } from "@/types";
import { MOCK_DEALER_ID, saveMultipleLeadsToSupabase } from "@/services/leads";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/use-window-size";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export const CSVUploader = () => {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isMappingCSV, setIsMappingCSV] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showDuplicatesDialog, setShowDuplicatesDialog] = useState(false);
  const [uploadedLeadsCount, setUploadedLeadsCount] = useState(0);
  const [duplicateLeadsCount, setDuplicateLeadsCount] = useState(0);
  const [duplicateLeads, setDuplicateLeads] = useState<Array<{lead: Lead, existingLead: Lead}>>([]);
  const { width, height } = useWindowSize();

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    try {
      console.log("CSV file selected:", files[0].name, "Size:", files[0].size, "bytes");
      setCsvFile(files[0]);
      setIsMappingCSV(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error processing the selected file";
      console.error("CSV file selection error:", err);
      setError(errorMessage);
      toast({
        title: "File error",
        description: errorMessage,
        variant: "destructive",
      });
    }
    
    event.target.value = "";
  };
  
  const handleCancelMapping = () => {
    setCsvFile(null);
    setIsMappingCSV(false);
    setError(null);
  };
  
  const handleCompletedMapping = async (mappedLeads: Lead[]) => {
    setIsUploading(true);
    setError(null);
    
    try {
      console.log(`Processing ${mappedLeads.length} mapped leads`);
      const processedLeads = mappedLeads.map(lead => ({
        ...lead,
        meta_notes: lead.meta_notes === "none" ? "" : lead.meta_notes,
        dealer_id: MOCK_DEALER_ID
      }));
      
      console.log("First lead to save:", processedLeads[0]);
      const { success, error, count, duplicateCount, duplicates } = await saveMultipleLeadsToSupabase(processedLeads);
      
      if (success) {
        setShowConfetti(true);
        setUploadedLeadsCount(count);
        
        // Check if there were any duplicates
        if (duplicateCount > 0 && duplicates) {
          setDuplicateLeadsCount(duplicateCount);
          setDuplicateLeads(duplicates);
          setShowDuplicatesDialog(true);
        } else {
          setShowSuccessDialog(true);
        }
        
        setCsvFile(null);
        setIsMappingCSV(false);
      } else if (error === "All leads were duplicates of existing records" && duplicates) {
        // If all leads were duplicates
        setDuplicateLeadsCount(duplicateCount || 0);
        setDuplicateLeads(duplicates);
        setShowDuplicatesDialog(true);
        setCsvFile(null);
        setIsMappingCSV(false);
      } else {
        throw new Error(error);
      }
    } catch (error) {
      console.error("Failed to upload leads:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      setError(errorMessage);
      toast({
        title: "Failed to upload leads",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    navigate("/dashboard");
  };

  const handleDuplicatesDialogClose = () => {
    setShowDuplicatesDialog(false);
    if (uploadedLeadsCount > 0) {
      // If we had some successful uploads alongside duplicates
      setShowSuccessDialog(true);
    } else {
      // If all were duplicates
      navigate("/dashboard");
    }
  };

  const getLeadStatusLabel = (status: string) => {
    switch (status) {
      case "queued": return "queued for contact";
      case "in-progress": return "being contacted";
      case "completed": return "successfully contacted";
      case "reactivated": return "reactivated";
      case "not-interested": return "marked as not interested";
      default: return `in ${status} status`;
    }
  };

  if (isMappingCSV && csvFile) {
    return (
      <CSVMapper 
        file={csvFile}
        onCancel={handleCancelMapping}
        onComplete={handleCompletedMapping}
        dealerId={MOCK_DEALER_ID}
      />
    );
  }

  return (
    <>
      {showConfetti && <Confetti width={width} height={height} recycle={false} numberOfPieces={500} />}
      
      <AlertDialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl flex items-center gap-2 text-center">
              <PartyPopper className="h-6 w-6 text-green-500 mx-auto" />
              <span className="flex-1 text-center">Success!</span>
              <CheckCircle className="h-6 w-6 text-green-500 mx-auto" />
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center py-4 text-base">
              <p className="text-lg font-medium text-green-600 mb-2">
                {uploadedLeadsCount} leads have been successfully uploaded!
              </p>
              <p className="mt-2">
                Your leads are now queued for AI-powered calls and will appear on your dashboard.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-center">
            <AlertDialogAction 
              onClick={handleSuccessDialogClose}
              className="airbnb-button bg-green-600 hover:bg-green-700 px-8"
            >
              View on Dashboard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDuplicatesDialog} onOpenChange={setShowDuplicatesDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl flex items-center gap-2 text-center">
              <AlertTriangle className="h-6 w-6 text-amber-500 mx-auto" />
              <span className="flex-1 text-center">Duplicate Leads Found</span>
              <AlertTriangle className="h-6 w-6 text-amber-500 mx-auto" />
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center py-4 text-base">
              <p className="text-lg font-medium text-amber-600 mb-2">
                {duplicateLeadsCount} duplicate leads were detected
              </p>
              
              {duplicateLeads && duplicateLeads.length > 0 && (
                <div className="max-h-60 overflow-y-auto mt-4 text-left bg-gray-50 p-3 rounded">
                  {duplicateLeads.slice(0, 5).map((item, index) => (
                    <div key={index} className="mb-3 pb-3 border-b border-gray-200 last:border-b-0">
                      <p className="text-sm font-medium text-gray-800">{item.lead.name}</p>
                      <p className="text-xs text-gray-600">{item.lead.email}</p>
                      <p className="text-xs text-gray-600">{item.lead.phone}</p>
                      <p className="text-xs text-gray-500 mt-1">Already {getLeadStatusLabel(item.existingLead.status)}</p>
                    </div>
                  ))}
                  {duplicateLeads.length > 5 && (
                    <p className="text-xs text-center text-gray-500 mt-2">
                      And {duplicateLeads.length - 5} more...
                    </p>
                  )}
                </div>
              )}
              
              {uploadedLeadsCount > 0 ? (
                <p className="mt-4">
                  {uploadedLeadsCount} new leads were successfully imported.
                </p>
              ) : (
                <p className="mt-4">
                  No new leads were imported. All leads in this file already exist in your system.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-between">
            <AlertDialogCancel onClick={() => setShowDuplicatesDialog(false)}>
              Close
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDuplicatesDialogClose}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {uploadedLeadsCount > 0 ? "Continue" : "Go to Dashboard"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="airbnb-card">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">Upload CSV</CardTitle>
          <CardDescription>
            Upload multiple leads at once using a CSV file
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-12">
          {error && (
            <Alert variant="destructive" className="mb-4 w-full">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="w-full max-w-sm mx-auto border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary transition-colors">
            <Upload className="h-10 w-10 text-gray-400 mb-2 mx-auto" />
            <h3 className="text-lg font-medium mb-1">Upload your file</h3>
            <p className="text-sm text-gray-500 mb-4">CSV with your lead data - any format</p>
            
            <Input
              type="file"
              accept=".csv"
              onChange={handleCsvUpload}
              disabled={isUploading}
              className="hidden"
              id="csv-upload"
            />
            <label htmlFor="csv-upload">
              <Button 
                variant="outline" 
                className="airbnb-button cursor-pointer" 
                disabled={isUploading}
                asChild
              >
                <span>{isUploading ? 'Uploading...' : 'Select CSV File'}</span>
              </Button>
            </label>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2 text-sm text-gray-500">
          <p>Don't worry about the CSV format! Our smart mapping tool will help you match your columns to our fields.</p>
          <p>You'll need to provide at minimum: <span className="font-medium">Name, Email, and Phone</span> for each lead.</p>
          <p className="text-amber-600 font-medium">Note: Duplicate leads (with same email or phone) will be detected and skipped.</p>
          <p className="text-gray-600">CSV Tips: Avoid blank lines, ensure all rows have the same number of columns, and use quotes around fields that contain commas.</p>
        </CardFooter>
      </Card>
    </>
  );
};
